// @ts-nocheck
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import type { DeckCard, DeckCardPrinting, Deck } from '@/types/deck';
import { useUIStore } from '@/store/uiStore';

interface DeckValidation {
	isValid: boolean;
	canSave: boolean;
	canMakePublic: boolean;
	errors: Array<{ type: string; severity: string; message: string; cardNames?: string[] }>;
	warnings: Array<{ type: string; severity: string; message: string }>;
	inkColors: Set<string>;
	totalCards: number;
}

interface ActiveDeck {
	id: string | null;
	name: string;
	description: string;
	paragon_canonical_id: string | null;
	format: 'core' | 'infinity';
	is_public: boolean;
	visibility: 'private' | 'unlisted' | 'public';
	status: 'draft' | 'published' | 'archived';
	slug: string | null;
	hero_printing_id: string | null;
	sourceDeckId: string | null; // Track the source deck when copying
	cards: DeckCard[];
}

interface DeckStore {
	activeDeck: ActiveDeck | null;
	baselineCards: DeckCard[] | null;
	isPanelOpen: boolean;
	isLoading: boolean;
	isSaving: boolean;
	pendingCardUpdates: Promise<void>[];
	hasPanelBeenOpened: boolean;
	isSyncing: () => boolean;

	openPanel: (deckId?: string) => Promise<void>;
	loadDeckCopy: (deckData: any, printingsData: any[]) => void;
	reloadDeck: () => Promise<void>;
	closePanel: () => void;
	createNewDeck: () => void;

	addCardToDeck: (
		canonicalCardId: string,
		printingId: string,
		isFoil?: boolean
	) => Promise<boolean>;
	removeCardFromDeck: (canonicalCardId: string) => void;
	updateCardQuantity: (canonicalCardId: string, quantity: number) => void;
	adjustCardQuantity: (canonicalCardId: string, delta: number, printingId?: string, isFoil?: boolean) => Promise<void>;
	hydrateCardPrinting: (canonicalCardId: string) => Promise<void>;
	updatePrintingAndQuantities: (
		canonicalCardId: string,
		printingId: string | null,
		normalQty: number,
		foilQty: number
	) => Promise<void>;
	setPreferredPrinting: (canonicalCardId: string, printingId: string) => Promise<void>;
	setParagon: (canonicalCardId: string | null) => void;
	setHeroPrinting: (printingId: string) => void;

	updateDeckName: (name: string) => void;
	updateDeckDescription: (description: string) => void;
	setDeckPublic: (isPublic: boolean, validation?: DeckValidation) => Promise<void>;
	setDeckFormat: (format: 'core' | 'infinity') => void;
	setDeckVisibility: (visibility: 'private' | 'unlisted' | 'public') => Promise<void>;
	setDeckStatus: (status: 'draft' | 'published' | 'archived') => Promise<void>;
	publishDeck: (validation: DeckValidation) => Promise<void>;

	saveDeck: () => Promise<{ success: boolean; deckId: string; slug: string | null } | void>;
	deleteDeck: () => Promise<void>;
	clearDeck: () => void;
	batchImportCards: (
		cardsToImport: Array<{ canonical_card_id: string; quantity: number; printing_id: string }>
	) => Promise<{ added: number; failed: number }>;
}

// Printing cache to avoid redundant queries
const printingCache = new Map<string, any>();
const canonicalCache = new Map<string, any>();

// Per-card update queues to serialize writes and prevent overlapping updates
const updateQueues = new Map<string, Promise<void>>();
const commitTimers = new Map<string, ReturnType<typeof setTimeout>>();
const pendingTargets = new Map<
	string,
	{ canonicalCardId: string; printingId: string; normalQty: number; foilQty: number }
>();

// Helper to enqueue card updates per canonical_card_id
const enqueueCardUpdate = (cardId: string, task: () => Promise<void>): Promise<void> => {
	const prev = updateQueues.get(cardId) || Promise.resolve();
	const next = prev
		.then(task)
		.catch((e) => {
			console.error('[deckStore] Card update error:', e);
		})
		.finally(() => {
			// Clean up queue if it's the last in chain
			if (updateQueues.get(cardId) === next) {
				updateQueues.delete(cardId);
			}
		});
	updateQueues.set(cardId, next);
	return next;
};

// Sync monitoring and logging
const isDev = import.meta.env.DEV;

interface SyncMetrics {
	startTime: number;
	operation: string;
	deckId?: string;
}

const syncMetrics = new Map<Promise<void>, SyncMetrics>();

const logSyncError = (message: string, error?: any) => {
	console.error(`[DeckSync ERROR] ${message}`, error || '');
};

// Helper to track promises with automatic cleanup and monitoring
const trackPromise = (
	promise: Promise<void>,
	set: (state: Partial<DeckStore>) => void,
	get: () => DeckStore,
	operation: string = 'unknown'
) => {
	const state = get();
	const deckId = state.activeDeck?.id;
	const startTime = Date.now();

	// Store metrics
	syncMetrics.set(promise, { startTime, operation, deckId });

	const trackedPromise = promise
		.then(() => {
			const metrics = syncMetrics.get(promise);
			if (metrics) {
				syncMetrics.delete(promise);
			}
		})
		.catch((error) => {
			const metrics = syncMetrics.get(promise);
			if (metrics) {
				const duration = Date.now() - metrics.startTime;
				logSyncError(`Failed: ${operation}`, {
					deckId: metrics.deckId,
					duration: `${duration}ms`,
					error: error.message
				});
				syncMetrics.delete(promise);
			}
			throw error;
		})
		.finally(() => {
			// Remove this specific promise from the array after completion
			const currentState = get();
			const newPending = currentState.pendingCardUpdates.filter((p) => p !== trackedPromise);

			set({
				pendingCardUpdates: newPending
			});
		});

	// Add timeout warning (10 seconds)
	setTimeout(() => {
		if (syncMetrics.has(promise)) {
			const metrics = syncMetrics.get(promise)!;
			const duration = Date.now() - metrics.startTime;
			logSyncError(`Slow operation detected: ${metrics.operation}`, {
				deckId: metrics.deckId,
				duration: `${duration}ms`
			});
		}
	}, 10000);

	set({
		pendingCardUpdates: [...get().pendingCardUpdates, trackedPromise]
	});

	return trackedPromise;
};

// Helper to drain all pending updates
const drainPendingAndQueues = async (getState: () => any) => {
	// Wait for pendingCardUpdates
	const pending = getState().pendingCardUpdates;
	if (pending.length > 0) {
		await Promise.allSettled(pending);
		getState().set({ pendingCardUpdates: [] });
	}

	// Wait for all per-card queues
	const allQueues = Array.from(updateQueues.values());
	if (allQueues.length > 0) {
		await Promise.allSettled(allQueues);
	}
};

// Throttled reload to avoid showing intermediate states
let reloadTimer: ReturnType<typeof setTimeout> | null = null;
const scheduleReload = (deckId: string, getState: () => any) => {
	if (reloadTimer) clearTimeout(reloadTimer);
	reloadTimer = setTimeout(async () => {
		reloadTimer = null;
		// Only reload if no queues are active
		if (updateQueues.size === 0) {
			await getState().reloadDeck();
		}
	}, 150);
};

export const useDeckStore = create<DeckStore>()(
	persist(
		(set, get) => ({
			activeDeck: null,
			baselineCards: null,
			isPanelOpen: false,
			isLoading: false,
			isSaving: false,
			pendingCardUpdates: [],
			hasPanelBeenOpened: false,

			isSyncing: () => {
				return get().pendingCardUpdates.length > 0 || updateQueues.size > 0;
			},

			createNewDeck: () => {
				set({
					activeDeck: {
						id: null,
						name: '',
						description: '',
						paragon_canonical_id: null,
						format: 'core' as 'core' | 'infinity',
						is_public: false,
						visibility: 'private' as 'private' | 'unlisted' | 'public',
						status: 'draft' as 'draft' | 'published' | 'archived',
						slug: null,
						hero_printing_id: null,
						sourceDeckId: null,
						cards: []
					},
					baselineCards: []
				});
			},

		openPanel: async (deckId?: string) => {
			set({ hasPanelBeenOpened: true });
			
			// Switch microbar to deck mode when panel opens
			useUIStore.getState().setMicrobarMode('deck');
			
			if (deckId) {
					set({ isLoading: true, isPanelOpen: true });

					// Drain all pending updates before loading
					const hadPending = get().pendingCardUpdates.length > 0 || updateQueues.size > 0;
					await drainPendingAndQueues(get);

					// Reconcile if we had pending updates
					if (hadPending) {
						await supabase.rpc('reconcile_deck_cards', { p_deck_id: deckId });
					}

					try {
						const { data, error } = await supabase
							.from('decks')
							.select(
								`
                *,
                deck_cards(
                  *,
                  canonical_cards(*),
                  cards:cards!deck_cards_preferred_printing_id_fkey(*, sets(name, code, icon_url))
                )
              `
							)
							.eq('id', deckId)
							.single();

						if (error) throw error;

						// Load child printings for each canonical card
						const { data: printingsData } = await supabase
							.from('deck_card_printings')
							.select('*, cards(*, sets(name, code, icon_url))')
							.eq('deck_id', deckId);

						// Group printings by canonical_card_id
						const printingsByCanonical = new Map<string, any[]>();
						for (const p of printingsData || []) {
							if (!printingsByCanonical.has(p.canonical_card_id)) {
								printingsByCanonical.set(p.canonical_card_id, []);
							}
							printingsByCanonical.get(p.canonical_card_id)!.push(p);
						}

						// Normalize cards shape and attach printings
						let deckCards = (data.deck_cards || []).map((c) => ({
							...c,
							cards: Array.isArray(c.cards) ? c.cards[0] : c.cards,
							printings: printingsByCanonical.get(c.canonical_card_id) || []
						}));

						// Filter out zero-quantity printings and clean up state
						deckCards = deckCards.map((c) => {
							// Remove zero-quantity printings
							const nonZeroPrintings = (c.printings || []).filter((p) => {
								const qty = (p.normal_quantity || 0) + (p.foil_quantity || 0);
								return qty > 0;
							});

							// Derive quantity from remaining printings
							const derivedQtyFromPrintings = nonZeroPrintings.reduce(
								(s, p) => s + (p.normal_quantity || 0) + (p.foil_quantity || 0),
								0
							);
							const derivedQtyFromParent = (c.normal_quantity || 0) + (c.foil_quantity || 0);
							const qty = derivedQtyFromPrintings || derivedQtyFromParent || c.quantity || 0;

							// Fix preferred_printing_id if it points to a zero-quantity printing
							let preferredPrintingId = c.preferred_printing_id;
							let displayCards = c.cards;

							if (
								preferredPrintingId &&
								!nonZeroPrintings.find((p) => p.printing_id === preferredPrintingId)
							) {
								// Preferred printing is zero or missing, switch to first non-zero
								if (nonZeroPrintings.length > 0) {
									preferredPrintingId = nonZeroPrintings[0].printing_id;
									displayCards = nonZeroPrintings[0].cards;
								} else {
									preferredPrintingId = null;
									displayCards = null;
								}
							}

							return {
								...c,
								quantity: qty,
								printings: nonZeroPrintings,
								preferred_printing_id: preferredPrintingId,
								cards: displayCards
							};
						});

						// Hydrate missing printing data
						const missingPrintings = deckCards.filter((c) => !c.cards && c.preferred_printing_id);
						if (missingPrintings.length > 0) {
							const printingIds = missingPrintings.map((c) => c.preferred_printing_id);
							const { data: printings } = await supabase
								.from('cards')
								.select('*, sets(name, code, icon_url)')
								.in('id', printingIds);

							if (printings) {
								const printingMap = new Map(printings.map((p) => [p.id, p]));
								deckCards = deckCards.map((c) => ({
									...c,
									cards: c.cards || printingMap.get(c.preferred_printing_id) || null
								}));
							}
						}

						set({
							activeDeck: {
								id: data.id,
								name: data.name,
								description: data.description || '',
								paragon_canonical_id: data.paragon_canonical_id,
								format: data.format as 'core' | 'infinity',
								is_public: data.is_public,
								visibility: (data as any).visibility || 'private',
								status: (data as any).status || 'draft',
								slug: (data as any).slug || null,
								hero_printing_id: (data as any).hero_printing_id || null,
								sourceDeckId: null,
								cards: deckCards
							},
							baselineCards: JSON.parse(JSON.stringify(deckCards)),
							isLoading: false
						});
					} catch (error) {
						console.error('Error loading deck:', error);
						toast.error('Failed to load deck');
						set({ isLoading: false });
					}
				} else {
					const state = get();
					if (!state.activeDeck) {
						state.createNewDeck();
					} else {
						// Normalize persisted deck cards shape
						const normalized = state.activeDeck.cards.map((c) => ({
							...c,
							cards: Array.isArray(c.cards) ? c.cards[0] : c.cards
						}));

						set({ activeDeck: { ...state.activeDeck, cards: normalized } });

						// Hydrate persisted deck if needed
						const missingPrintings = normalized.filter((c) => !c.cards && c.preferred_printing_id);
						if (missingPrintings.length > 0) {
							const printingIds = missingPrintings.map((c) => c.preferred_printing_id);
							const { data: printings } = await supabase
								.from('cards')
								.select('*, sets(name, code, icon_url)')
								.in('id', printingIds);

							if (printings) {
								const printingMap = new Map(printings.map((p) => [p.id, p]));
								const hydratedCards = normalized.map((c) => ({
									...c,
									cards: c.cards || printingMap.get(c.preferred_printing_id) || null
								}));

								set({
									activeDeck: { ...state.activeDeck, cards: hydratedCards }
								});
							}
						}
					}
					set({ isPanelOpen: true });
				}
			},

		loadDeckCopy: (deckData: any, printingsData: any[]) => {
			// Switch microbar to deck mode when loading a deck copy
			useUIStore.getState().setMicrobarMode('deck');
			
			// Group printings by canonical_card_id
			const printingsByCanonical = new Map<string, any[]>();
				for (const p of printingsData) {
					if (!printingsByCanonical.has(p.canonical_card_id)) {
						printingsByCanonical.set(p.canonical_card_id, []);
					}
					printingsByCanonical.get(p.canonical_card_id)!.push(p);
				}

				// Normalize cards shape and attach printings - CONVERT ALL TO NON-FOIL FOR BUDGET CLONING
				const deckCards = (deckData.deck_cards || []).map((c) => ({
					...c,
					id: crypto.randomUUID(), // Generate new IDs for the copy
					deck_id: null, // Clear deck_id since this is a new deck
					normal_quantity: c.quantity || (c.normal_quantity || 0) + (c.foil_quantity || 0), // Convert all to normal
					foil_quantity: 0, // Zero out foil for budget version
					cards: Array.isArray(c.cards) ? c.cards[0] : c.cards,
					printings: (printingsByCanonical.get(c.canonical_card_id) || []).map((p) => ({
						...p,
						id: crypto.randomUUID(), // Generate new IDs for printings
						deck_id: null,
						normal_quantity: (p.normal_quantity || 0) + (p.foil_quantity || 0), // Convert all to normal
						foil_quantity: 0 // Zero out foil for budget version
					}))
				}));

				// Smart name truncation to ensure "Clone of X" never exceeds 30 characters
				const maxOriginalLength = 30 - 9; // "Clone of " = 9 chars
				let truncatedName = deckData.name;
				if (truncatedName.length > maxOriginalLength) {
					truncatedName = truncatedName.substring(0, maxOriginalLength - 1) + '…';
				}
				const clonedName = `Clone of ${truncatedName}`;
				// Final safety check
				const finalName = clonedName.length > 30 ? clonedName.substring(0, 30) : clonedName;

				set({
					activeDeck: {
						id: null, // No ID - this is a new deck
						name: finalName,
						description: deckData.description || '',
						paragon_canonical_id: deckData.paragon_canonical_id,
						format: deckData.format as 'core' | 'infinity',
						is_public: false, // Start as private
						visibility: 'private',
						status: 'draft',
						slug: null,
						hero_printing_id: deckData.hero_printing_id || null,
						sourceDeckId: deckData.id, // Track the source deck ID
						cards: deckCards
					},
					baselineCards: JSON.parse(JSON.stringify(deckCards)),
					isPanelOpen: true,
					isLoading: false
				});
			},

			reloadDeck: async () => {
				const state = get();
				if (!state.activeDeck?.id) return;

				try {
					const { data, error } = await supabase
						.from('decks')
						.select(
							`
              *,
              deck_cards(
                *,
                canonical_cards(*),
                cards:cards!deck_cards_preferred_printing_id_fkey(*, sets(name, code, icon_url))
              )
            `
						)
						.eq('id', state.activeDeck.id)
						.maybeSingle();

					// Check if deck no longer exists
					if (error?.code === 'PGRST116' || !data) {
						get().clearDeck();
						toast.error('Deck no longer exists');
						return;
					}

					if (error) throw error;

					// Load child printings for each canonical card
					const { data: printingsData } = await supabase
						.from('deck_card_printings')
						.select('*, cards(*, sets(name, code, icon_url))')
						.eq('deck_id', state.activeDeck.id);

					// Group printings by canonical_card_id
					const printingsByCanonical = new Map<string, any[]>();
					for (const p of printingsData || []) {
						if (!printingsByCanonical.has(p.canonical_card_id)) {
							printingsByCanonical.set(p.canonical_card_id, []);
						}
						printingsByCanonical.get(p.canonical_card_id)!.push(p);
					}

					// Normalize cards shape and attach printings
					let deckCards = (data.deck_cards || []).map((c) => ({
						...c,
						cards: Array.isArray(c.cards) ? c.cards[0] : c.cards,
						printings: printingsByCanonical.get(c.canonical_card_id) || []
					}));

					// Filter out zero-quantity printings and clean up state
					deckCards = deckCards.map((c) => {
						const nonZeroPrintings = (c.printings || []).filter((p) => {
							const qty = (p.normal_quantity || 0) + (p.foil_quantity || 0);
							return qty > 0;
						});

						const derivedQtyFromPrintings = nonZeroPrintings.reduce(
							(s, p) => s + (p.normal_quantity || 0) + (p.foil_quantity || 0),
							0
						);
						const derivedQtyFromParent = (c.normal_quantity || 0) + (c.foil_quantity || 0);
						const qty = derivedQtyFromPrintings || derivedQtyFromParent || c.quantity || 0;

						let preferredPrintingId = c.preferred_printing_id;
						let displayCards = c.cards;

						if (
							preferredPrintingId &&
							!nonZeroPrintings.find((p) => p.printing_id === preferredPrintingId)
						) {
							if (nonZeroPrintings.length > 0) {
								preferredPrintingId = nonZeroPrintings[0].printing_id;
								displayCards = nonZeroPrintings[0].cards;
							} else {
								preferredPrintingId = null;
								displayCards = null;
							}
						}

						return {
							...c,
							quantity: qty,
							printings: nonZeroPrintings,
							preferred_printing_id: preferredPrintingId,
							cards: displayCards
						};
					});

					// Hydrate missing printing data
					const missingPrintings = deckCards.filter((c) => !c.cards && c.preferred_printing_id);
					if (missingPrintings.length > 0) {
						const printingIds = missingPrintings.map((c) => c.preferred_printing_id);
						const { data: printings } = await supabase
							.from('cards')
							.select('*, sets(name, code, icon_url)')
							.in('id', printingIds);

						if (printings) {
							const printingMap = new Map(printings.map((p) => [p.id, p]));
							deckCards = deckCards.map((c) => ({
								...c,
								cards: c.cards || printingMap.get(c.preferred_printing_id) || null
							}));
						}
					}

					// Update activeDeck but DO NOT update baselineCards
					set({
						activeDeck: {
							id: data.id,
							name: data.name,
							description: data.description || '',
							paragon_canonical_id: data.paragon_canonical_id,
							format: data.format as 'core' | 'infinity',
							is_public: data.is_public,
							visibility: (data as any).visibility || 'private',
							status: (data as any).status || 'draft',
							slug: (data as any).slug || null,
							hero_printing_id: (data as any).hero_printing_id || null,
							sourceDeckId: null,
							cards: deckCards
						}
					});
				} catch (error) {
					console.error('Error reloading deck:', error);
					toast.error('Failed to reload deck');
				}
			},

			closePanel: async () => {
				const state = get();

				// Drain pending updates before closing
				await drainPendingAndQueues(get);

				// Check if deck exists in database before closing
				if (state.activeDeck?.id) {
					try {
						const { data, error } = await supabase
							.from('decks')
							.select('id')
							.eq('id', state.activeDeck.id)
							.maybeSingle();

						// If deck doesn't exist anymore, clear it
						if (!data || error?.code === 'PGRST116') {
							get().clearDeck();
							return;
						}
					} catch (error) {
						console.error('[deckStore] Error checking deck existence:', error);
					}
				}

				set({ isPanelOpen: false });
			},

			addCardToDeck: async (
				canonicalCardId: string,
				printingId: string,
				userSelectedFoil?: boolean
			) => {
				let state = get();

				// Auto-create a new deck if none exists
				if (!state.activeDeck) {
					get().createNewDeck();
					// Re-read state so we don't use a stale snapshot
					state = get();
					if (!state.activeDeck) return false;
				}

				// Validate that printingId is actually a printing ID, not a canonical ID
				if (!printingId || printingId.startsWith('canonical_')) {
					console.error('[deckStore] Invalid printing ID - received canonical ID:', printingId);
					toast.error('Failed to add card: Invalid printing reference');
					return false;
				}

				try {
					// Check if parent row exists
					let parentCard = state.activeDeck.cards.find(
						(c) => c.canonical_card_id === canonicalCardId
					);

					if (!state.activeDeck.id) {
						// New unsaved deck: add in-memory
						if (!parentCard) {
							// Fetch printing and canonical data
							const { data: printing } = await supabase
								.from('cards')
								.select('*, sets(name, code, icon_url)')
								.eq('id', printingId)
								.single();

							const { data: canonical } = await supabase
								.from('canonical_cards')
								.select('*')
								.eq('id', canonicalCardId)
								.single();

							if (!printing || !canonical) {
								toast.error('Failed to fetch card data');
								return false;
							}

							const isFoil =
								userSelectedFoil !== undefined ? userSelectedFoil : printing.finish === 'foil';
							const newCard: DeckCard = {
								id: crypto.randomUUID(),
								deck_id: state.activeDeck.id || '',
								canonical_card_id: canonicalCardId,
								quantity: 1,
								preferred_printing_id: printingId,
								canonical_cards: canonical,
								cards: printing,
								printings: [
									{
										id: crypto.randomUUID(),
										deck_id: state.activeDeck.id || '',
										canonical_card_id: canonicalCardId,
										printing_id: printingId,
										normal_quantity: isFoil ? 0 : 1,
										foil_quantity: isFoil ? 1 : 0,
										quantity: 1,
										cards: printing,
										created_at: new Date().toISOString(),
										updated_at: new Date().toISOString()
									}
								]
							};
							// Auto-set as hero if deck doesn't have one yet
							const shouldSetHero = !state.activeDeck.hero_printing_id;

							set({
								activeDeck: {
									...state.activeDeck,
									hero_printing_id: shouldSetHero ? printingId : state.activeDeck.hero_printing_id,
									cards: [...state.activeDeck.cards, newCard]
								}
							});

							if (shouldSetHero) {
								toast.success('Card added as deck hero image');
							}
						} else {
							// Increment existing printing or add new printing
							const existingPrinting = parentCard.printings?.find(
								(p) => p.printing_id === printingId
							);
							const totalQty =
								parentCard.printings?.reduce(
									(sum, p) => sum + (p.normal_quantity || 0) + (p.foil_quantity || 0),
									0
								) || 0;

							// Check limit BEFORE showing brief >4 state
							if (totalQty >= 4) {
								toast.error(
									`Cannot add more than 4 copies of "${parentCard.canonical_cards?.name || 'this card'}"`
								);
								return false;
							}

							if (existingPrinting) {
								// Increment existing printing (respect user selection or printing finish)
								const isFoil =
									userSelectedFoil !== undefined
										? userSelectedFoil
										: existingPrinting.cards?.finish === 'foil';
								const maxCanAdd = 4 - totalQty + existingPrinting.quantity;
								if (isFoil) {
									existingPrinting.foil_quantity = Math.min(
										maxCanAdd,
										existingPrinting.foil_quantity + 1
									);
								} else {
									existingPrinting.normal_quantity = Math.min(
										maxCanAdd,
										existingPrinting.normal_quantity + 1
									);
								}
								existingPrinting.quantity =
									existingPrinting.normal_quantity + existingPrinting.foil_quantity;

								// Switch preferred printing to the last-added printing
								parentCard.preferred_printing_id = printingId;
								parentCard.cards = existingPrinting.cards;
							} else {
								// Add new printing
								const { data: printing } = await supabase
									.from('cards')
									.select('*, sets(name, code, icon_url)')
									.eq('id', printingId)
									.single();

								if (!printing) return false;

								const isFoil =
									userSelectedFoil !== undefined ? userSelectedFoil : printing.finish === 'foil';
								parentCard.printings = parentCard.printings || [];
								parentCard.printings.push({
									id: crypto.randomUUID(),
									deck_id: state.activeDeck.id || '',
									canonical_card_id: canonicalCardId,
									printing_id: printingId,
									normal_quantity: isFoil ? 0 : 1,
									foil_quantity: isFoil ? 1 : 0,
									quantity: 1,
									cards: printing,
									created_at: new Date().toISOString(),
									updated_at: new Date().toISOString()
								});

								// Switch preferred printing to the last-added printing
								parentCard.preferred_printing_id = printingId;
								parentCard.cards = printing;
							}

							// Recalculate parent total
							parentCard.quantity = parentCard.printings.reduce(
								(sum, p) => sum + (p.normal_quantity || 0) + (p.foil_quantity || 0),
								0
							);
							set({ activeDeck: { ...state.activeDeck, cards: [...state.activeDeck.cards] } });
						}
						return true;
					}

					// Saved deck: Optimistic update with background DB sync
					// Step 1: Fetch printing data and canonical card data for optimistic update
					const { data: printing } = await supabase
						.from('cards')
						.select('*, sets(name, code, icon_url)')
						.eq('id', printingId)
						.single();

					if (!printing) return false;

					const { data: canonical } = await supabase
						.from('canonical_cards')
						.select('*')
						.eq('id', canonicalCardId)
						.single();

					if (!canonical) {
						toast.error('Failed to fetch card data');
						return false;
					}

					const isFoil =
						userSelectedFoil !== undefined ? userSelectedFoil : printing.finish === 'foil';

					// Step 2: Optimistic local state update
					if (!parentCard) {
						// Add new card to deck
						const newCard = {
							id: crypto.randomUUID(),
							deck_id: state.activeDeck.id || '',
							canonical_card_id: canonicalCardId,
							quantity: 1,
							preferred_printing_id: printingId,
							normal_quantity: isFoil ? 0 : 1,
							foil_quantity: isFoil ? 1 : 0,
							cards: printing,
							canonical_cards: canonical,
							printings: [
								{
									id: crypto.randomUUID(),
									deck_id: state.activeDeck.id || '',
									canonical_card_id: canonicalCardId,
									printing_id: printingId,
									normal_quantity: isFoil ? 0 : 1,
									foil_quantity: isFoil ? 1 : 0,
									quantity: 1,
									cards: printing,
									created_at: new Date().toISOString(),
									updated_at: new Date().toISOString()
								}
							],
							created_at: new Date().toISOString(),
							updated_at: new Date().toISOString()
						};

						// Auto-set as hero if deck doesn't have one yet
						const shouldSetHero = !state.activeDeck.hero_printing_id;

						set({
							activeDeck: {
								...state.activeDeck,
								hero_printing_id: shouldSetHero ? printingId : state.activeDeck.hero_printing_id,
								cards: [...state.activeDeck.cards, newCard]
							}
						});

						// Step 3: Background DB sync
						const syncPromise = (async () => {
							try {
								const { data: newParent, error: parentError } = await supabase
									.from('deck_cards')
									.insert({
										deck_id: state.activeDeck.id,
										canonical_card_id: canonicalCardId,
										preferred_printing_id: printingId,
										normal_quantity: isFoil ? 0 : 1,
										foil_quantity: isFoil ? 1 : 0
									})
									.select()
									.single();

								if (parentError) throw parentError;

								const { error: childError } = await supabase.from('deck_card_printings').insert({
									deck_id: state.activeDeck.id,
									canonical_card_id: canonicalCardId,
									printing_id: printingId,
									normal_quantity: isFoil ? 0 : 1,
									foil_quantity: isFoil ? 1 : 0
								});

								if (childError) throw childError;
							} catch (error) {
								console.error('Background DB sync error:', error);
								toast.error('Failed to sync card to database');
								// Reload to fix inconsistency
								await get().openPanel(state.activeDeck.id);
							}
						})();

						trackPromise(syncPromise, set, get, 'addCardToDeck:updateQuantity');

						if (shouldSetHero) {
							toast.success('Card added as deck hero image');
						}
						return true;
					}

					// Check total quantity across printings BEFORE updating state
					const totalQty =
						parentCard.printings?.reduce(
							(sum, p) => sum + (p.normal_quantity || 0) + (p.foil_quantity || 0),
							0
						) || 0;

					if (totalQty >= 4) {
						toast.error(
							`Cannot add more than 4 copies of "${parentCard.canonical_cards?.name || 'this card'}"`
						);
						return false;
					}

					// Check if this printing already exists
					const existingPrinting = parentCard.printings?.find((p) => p.printing_id === printingId);

					if (existingPrinting) {
						// Increment existing printing
						const maxCanAdd = 4 - totalQty + existingPrinting.quantity;
						if (isFoil) {
							existingPrinting.foil_quantity = Math.min(
								maxCanAdd,
								existingPrinting.foil_quantity + 1
							);
						} else {
							existingPrinting.normal_quantity = Math.min(
								maxCanAdd,
								existingPrinting.normal_quantity + 1
							);
						}
						existingPrinting.quantity =
							existingPrinting.normal_quantity + existingPrinting.foil_quantity;

						// Switch preferred printing
						parentCard.preferred_printing_id = printingId;
						parentCard.cards = existingPrinting.cards;

						// Background DB sync
						const syncPromise = (async () => {
							try {
								const { error } = await supabase
									.from('deck_card_printings')
									.update(
										isFoil
											? { foil_quantity: existingPrinting.foil_quantity }
											: { normal_quantity: existingPrinting.normal_quantity }
									)
									.eq('id', existingPrinting.id);

								if (error) throw error;

								// Update parent quantities
								const normalSum =
									parentCard.printings?.reduce((sum, p) => sum + (p.normal_quantity || 0), 0) || 0;
								const foilSum =
									parentCard.printings?.reduce((sum, p) => sum + (p.foil_quantity || 0), 0) || 0;

								await supabase
									.from('deck_cards')
									.update({
										normal_quantity: normalSum,
										foil_quantity: foilSum,
										preferred_printing_id: printingId
									})
									.eq('deck_id', state.activeDeck.id)
									.eq('canonical_card_id', canonicalCardId);
							} catch (error) {
								console.error('Background DB sync error:', error);
								toast.error('Failed to sync card to database');
								await get().openPanel(state.activeDeck.id);
							}
						})();

						trackPromise(syncPromise, set, get, 'addCardToDeck:updatePrinting');
					} else {
						// Add new printing
						parentCard.printings = parentCard.printings || [];
						parentCard.printings.push({
							id: crypto.randomUUID(),
							deck_id: state.activeDeck.id || '',
							canonical_card_id: canonicalCardId,
							printing_id: printingId,
							normal_quantity: isFoil ? 0 : 1,
							foil_quantity: isFoil ? 1 : 0,
							quantity: 1,
							cards: printing,
							created_at: new Date().toISOString(),
							updated_at: new Date().toISOString()
						});

						// Switch preferred printing
						parentCard.preferred_printing_id = printingId;
						parentCard.cards = printing;

						// Background DB sync
						const syncPromise = (async () => {
							try {
								const { error } = await supabase.from('deck_card_printings').insert({
									deck_id: state.activeDeck.id,
									canonical_card_id: canonicalCardId,
									printing_id: printingId,
									normal_quantity: isFoil ? 0 : 1,
									foil_quantity: isFoil ? 1 : 0
								});

								if (error) throw error;

								// Update parent quantities
								const normalSum =
									parentCard.printings?.reduce((sum, p) => sum + (p.normal_quantity || 0), 0) || 0;
								const foilSum =
									parentCard.printings?.reduce((sum, p) => sum + (p.foil_quantity || 0), 0) || 0;

								await supabase
									.from('deck_cards')
									.update({
										normal_quantity: normalSum,
										foil_quantity: foilSum,
										preferred_printing_id: printingId
									})
									.eq('deck_id', state.activeDeck.id)
									.eq('canonical_card_id', canonicalCardId);
							} catch (error) {
								console.error('Background DB sync error:', error);
								toast.error('Failed to sync card to database');
								await get().openPanel(state.activeDeck.id);
							}
						})();

						trackPromise(syncPromise, set, get, 'addCardToDeck:insertPrinting');
					}

					// Recalculate parent total
					parentCard.quantity = parentCard.printings.reduce(
						(sum, p) => sum + (p.normal_quantity || 0) + (p.foil_quantity || 0),
						0
					);
					set({ activeDeck: { ...state.activeDeck, cards: [...state.activeDeck.cards] } });
					return true;
				} catch (error) {
					console.error('Error adding card:', error);
					toast.error('Failed to add card to deck');
					return false;
				}
			},

			batchImportCards: async (
				cardsToImport: Array<{ canonical_card_id: string; quantity: number; printing_id: string }>
			) => {
				const state = get();
				if (!state.activeDeck) return { added: 0, failed: 0 };

				let added = 0;
				let failed = 0;

				try {
					// For unsaved decks: Build complete cards array in memory
					if (!state.activeDeck.id) {
						const newCards = [...state.activeDeck.cards];

						for (const cardToImport of cardsToImport) {
							try {
								const { canonical_card_id, quantity, printing_id } = cardToImport;
								const qtyToAdd = Math.min(quantity, 4); // Cap at 4

								// Fetch printing data
								const { data: printing } = await supabase
									.from('cards')
									.select('*, sets(name, code, icon_url)')
									.eq('id', printing_id)
									.single();

								if (!printing) {
									failed++;
									continue;
								}

								// Fetch canonical data
								const { data: canonical } = await supabase
									.from('canonical_cards')
									.select('*')
									.eq('id', canonical_card_id)
									.single();

								if (!canonical) {
									failed++;
									continue;
								}

								// Check if card already exists in deck
								const existingCard = newCards.find(
									(c) => c.canonical_card_id === canonical_card_id
								);

								if (existingCard) {
									// Add to existing card
									const currentQty = existingCard.quantity || 0;
									const maxCanAdd = Math.min(qtyToAdd, 4 - currentQty);

									if (maxCanAdd > 0) {
										const existingPrinting = existingCard.printings?.find(
											(p) => p.printing_id === printing_id
										);

										if (existingPrinting) {
											existingPrinting.normal_quantity += maxCanAdd;
											existingPrinting.quantity =
												existingPrinting.normal_quantity + existingPrinting.foil_quantity;
										} else {
											existingCard.printings = existingCard.printings || [];
											existingCard.printings.push({
												id: crypto.randomUUID(),
												deck_id: '',
												canonical_card_id,
												printing_id,
												normal_quantity: maxCanAdd,
												foil_quantity: 0,
												quantity: maxCanAdd,
												cards: printing,
												created_at: new Date().toISOString(),
												updated_at: new Date().toISOString()
											});
										}

										existingCard.quantity = existingCard.printings.reduce(
											(sum, p) => sum + (p.normal_quantity || 0) + (p.foil_quantity || 0),
											0
										);
										existingCard.preferred_printing_id = printing_id;
										existingCard.cards = printing;
										added++;
									} else {
										failed++;
									}
								} else {
									// Add new card
									const newCard = {
										id: crypto.randomUUID(),
										deck_id: '',
										canonical_card_id,
										quantity: qtyToAdd,
										preferred_printing_id: printing_id,
										normal_quantity: qtyToAdd,
										foil_quantity: 0,
										cards: printing,
										canonical_cards: canonical,
										printings: [
											{
												id: crypto.randomUUID(),
												deck_id: '',
												canonical_card_id,
												printing_id,
												normal_quantity: qtyToAdd,
												foil_quantity: 0,
												quantity: qtyToAdd,
												cards: printing,
												created_at: new Date().toISOString(),
												updated_at: new Date().toISOString()
											}
										],
										created_at: new Date().toISOString(),
										updated_at: new Date().toISOString()
									};

									newCards.push(newCard);
									added++;
								}
							} catch (error) {
								console.error('Error importing card:', error);
								failed++;
							}
						}

						// Single state update with all cards
						set({
							activeDeck: {
								...state.activeDeck,
								cards: newCards
							}
						});

						return { added, failed };
					}

					// For saved decks: Use existing addCardToDeck but batch the state updates
					for (const cardToImport of cardsToImport) {
						try {
							const { canonical_card_id, quantity, printing_id } = cardToImport;
							const qtyToAdd = Math.min(quantity, 4);

							const success = await get().addCardToDeck(canonical_card_id, printing_id);

							if (success && qtyToAdd > 1) {
								await get().adjustCardQuantity(canonical_card_id, qtyToAdd - 1);
							}

							if (success) {
								added++;
							} else {
								failed++;
							}
						} catch (error) {
							console.error('Error importing card:', error);
							failed++;
						}
					}

					return { added, failed };
				} catch (error) {
					console.error('Batch import error:', error);
					return { added, failed };
				}
			},

			hydrateCardPrinting: async (canonicalCardId: string) => {
				const state = get();
				if (!state.activeDeck) return;

				const deck = state.activeDeck;
				const target = deck.cards.find((c) => c.canonical_card_id === canonicalCardId);
				if (!target) return;

				// Normalize if array
				const current = Array.isArray(target.cards) ? target.cards[0] : target.cards;

				if (import.meta.env.DEV) {
					console.debug('[deckStore] Hydrating card printing:', {
						canonicalCardId,
						cardName: target.canonical_cards?.name,
						preferredPrintingId: target.preferred_printing_id,
						hasCurrentPrinting: !!current,
						currentHasImages: !!(
							current?.image_small ||
							current?.image_normal ||
							current?.image_large
						)
					});
				}

				// Check if images are actually present before early return
				const hasImages = !!(
					current &&
					(current.image_small || current.image_normal || current.image_large)
				);

				// Don't hydrate if we have preferred printing ID, valid printing object, AND images
				if (
					target.preferred_printing_id &&
					current &&
					current.id === target.preferred_printing_id &&
					hasImages
				) {
					return;
				}

				// If no images, proceed with hydration even if IDs match
				if (!hasImages && target.preferred_printing_id) {
					// Continue to fetch printing data below
				} else if (hasImages) {
					return;
				}

				// Fetch by preferred_printing_id or fallback to default printing
				let printing = null;

				if (target.preferred_printing_id) {
					const { data, error } = await supabase
						.from('cards')
						.select('*, sets(name, code, icon_url)')
						.eq('id', target.preferred_printing_id)
						.maybeSingle();

					if (error && import.meta.env.DEV) {
						console.error(
							'[deckStore] Failed to fetch preferred printing:',
							target.preferred_printing_id,
							error
						);
					}

					printing = data;
				}

				// Fallback: only if preferred_printing_id was null/missing
				// Get the lowest collector number non-foil printing (base version)
				if (!printing && !target.preferred_printing_id) {
					const { data } = await supabase
						.from('cards')
						.select('*, sets(name, code, icon_url)')
						.eq('canonical_card_id', canonicalCardId)
						.or('finish.is.null,finish.neq.foil')
						.order('collector_number', { ascending: true })
						.limit(1)
						.maybeSingle();
					printing = data;
				}

				if (printing) {
					if (import.meta.env.DEV) {
						console.debug('[deckStore] Hydrated printing for', canonicalCardId, printing.id);
					}

					const updatedCards = deck.cards.map((c) =>
						c.canonical_card_id === canonicalCardId ? { ...c, cards: printing } : c
					);
					set({ activeDeck: { ...deck, cards: updatedCards } });
				}
			},

			removeCardFromDeck: async (canonicalCardId: string) => {
				const state = get();
				if (!state.activeDeck) return;

				// Check authentication
				const {
					data: { user }
				} = await supabase.auth.getUser();
				if (!user) {
					toast.error('Please sign in to manage decks');
					return;
				}

				// Unsaved deck: local filter
				if (!state.activeDeck.id) {
					const newCards = state.activeDeck.cards.filter(
						(c) => c.canonical_card_id !== canonicalCardId
					);

					// Clear hero if the last card is removed
					const shouldClearHero = newCards.length === 0;

					set({
						activeDeck: {
							...state.activeDeck,
							hero_printing_id: shouldClearHero ? null : state.activeDeck.hero_printing_id,
							cards: newCards
						}
					});
					return;
				}

				// Saved deck: delete from database
				try {
					await supabase
						.from('deck_card_printings')
						.delete()
						.eq('deck_id', state.activeDeck.id)
						.eq('canonical_card_id', canonicalCardId);

					await supabase
						.from('deck_cards')
						.delete()
						.eq('deck_id', state.activeDeck.id)
						.eq('canonical_card_id', canonicalCardId);

					await get().reloadDeck();
				} catch (error) {
					console.error('Error removing card from deck:', error);
					toast.error('Failed to remove card from deck');
				}
			},

			updateCardQuantity: (canonicalCardId: string, quantity: number) => {
				const state = get();
				if (!state.activeDeck) return;

				if (quantity < 1 || quantity > 4) {
					toast.error('Quantity must be between 1 and 4');
					return;
				}

				set({
					activeDeck: {
						...state.activeDeck,
						cards: state.activeDeck.cards.map((c) =>
							c.canonical_card_id === canonicalCardId ? { ...c, quantity } : c
						)
					}
				});
			},

		adjustCardQuantity: async (canonicalCardId: string, delta: number, printingId?: string, isFoil?: boolean) => {
			const state = get();
			if (!state.activeDeck) return;

			const card = state.activeDeck.cards.find((c) => c.canonical_card_id === canonicalCardId);
			if (!card) return;

			// For unsaved decks, update printings in-memory
			if (!state.activeDeck.id) {
				const targetPrintingId =
					printingId ||
					card.preferred_printing_id ||
					card.printings?.[0]?.printing_id ||
					(Array.isArray(card.cards) ? card.cards[0]?.id : card.cards?.id);

				if (!targetPrintingId) return;

					// Initialize printings array if needed
					card.printings = card.printings || [];

					let existing = card.printings.find((p) => p.printing_id === targetPrintingId);

					// Fallback: if preferred printing doesn't exist in printings array, use the first available
					if (!existing && card.printings.length > 0 && delta < 0) {
						const fallbackPrinting = card.printings[0];
						card.preferred_printing_id = fallbackPrinting.printing_id;
						card.cards = fallbackPrinting.cards;
						existing = fallbackPrinting;
					}

					const otherTotal = card.printings
						.filter((p) => p.printing_id !== targetPrintingId)
						.reduce((s, p) => s + (p.normal_quantity || 0) + (p.foil_quantity || 0), 0);
					const maxForThis = 4 - otherTotal;

					if (delta > 0) {
						// INCREMENT
						if (existing) {
							const isFoil = existing.cards?.finish === 'foil';
							if (isFoil) {
								const newFoil = Math.max(
									0,
									Math.min(
										maxForThis - existing.normal_quantity,
										(existing.foil_quantity || 0) + delta
									)
								);
								existing.foil_quantity = newFoil;
							} else {
								const newNormal = Math.max(
									0,
									Math.min(
										maxForThis - existing.foil_quantity,
										(existing.normal_quantity || 0) + delta
									)
								);
								existing.normal_quantity = newNormal;
							}
							existing.quantity = existing.normal_quantity + existing.foil_quantity;

							// OPTIMISTIC: Update UI immediately
							card.quantity = card.printings.reduce(
								(sum, p) => sum + (p.normal_quantity || 0) + (p.foil_quantity || 0),
								0
							);
							set({ activeDeck: { ...state.activeDeck, cards: [...state.activeDeck.cards] } });
						} else {
							// OPTIMISTIC: Create placeholder printing and update UI immediately
							const addQty = Math.max(0, Math.min(maxForThis, delta));
							if (addQty > 0) {
								// Create placeholder with minimal data
								const placeholderPrinting = {
									id: crypto.randomUUID(),
									deck_id: '',
									canonical_card_id: canonicalCardId,
									printing_id: targetPrintingId,
									normal_quantity: addQty,
									foil_quantity: 0,
									quantity: addQty,
									cards: Array.isArray(card.cards) ? card.cards[0] : card.cards,
									created_at: new Date().toISOString(),
									updated_at: new Date().toISOString()
								};

								card.printings.push(placeholderPrinting);
								card.quantity = card.printings.reduce(
									(sum, p) => sum + (p.normal_quantity || 0) + (p.foil_quantity || 0),
									0
								);
								set({ activeDeck: { ...state.activeDeck, cards: [...state.activeDeck.cards] } });

								// BACKGROUND: Fetch complete printing data
								setTimeout(async () => {
									try {
										const { data: printingObj } = await supabase
											.from('cards')
											.select('*, sets(name, code, icon_url)')
											.eq('id', targetPrintingId)
											.maybeSingle();

										if (printingObj) {
											const currentState = get();
											const currentCard = currentState.activeDeck?.cards.find(
												(c) => c.canonical_card_id === canonicalCardId
											);
											const currentPrinting = currentCard?.printings?.find(
												(p) => p.printing_id === targetPrintingId
											);

											if (currentPrinting) {
												// Update with full data, preserving quantities
												const isFoil = printingObj.finish === 'foil';
												currentPrinting.cards = printingObj;
												currentPrinting.normal_quantity = isFoil ? 0 : currentPrinting.quantity;
												currentPrinting.foil_quantity = isFoil ? currentPrinting.quantity : 0;

												set({ activeDeck: { ...currentState.activeDeck! } });
											}
										}
									} catch (error) {
										console.error('Background printing fetch failed:', error);
										// Don't revert - user already has the card added
									}
								}, 0);
							}
						}
				} else if (delta < 0 && existing) {
					// DECREMENT - already synchronous, just ensure immediate update
					let remaining = Math.abs(delta);
					let n = existing.normal_quantity || 0;
					let f = existing.foil_quantity || 0;

					// Use the explicitly provided finish preference if available
					const shouldRemoveFoil = isFoil !== undefined ? isFoil : (existing.cards?.finish === 'foil');
					
					if (shouldRemoveFoil) {
						const rmF = Math.min(remaining, f);
						f -= rmF;
						remaining -= rmF;

						if (remaining > 0 && n > 0) {
							const rmN = Math.min(remaining, n);
							n -= rmN;
						}
					} else {
						const rmN = Math.min(remaining, n);
						n -= rmN;
						remaining -= rmN;

						if (remaining > 0 && f > 0) {
							const rmF = Math.min(remaining, f);
							f -= rmF;
						}
					}

						existing.normal_quantity = n;
						existing.foil_quantity = f;
						existing.quantity = n + f;

						if (existing.quantity === 0) {
							card.printings = card.printings.filter((p) => p.printing_id !== targetPrintingId);

							if (card.preferred_printing_id === targetPrintingId) {
								if (card.printings.length > 0) {
									card.preferred_printing_id = card.printings[0].printing_id;
									card.cards = card.printings[0].cards;
								} else {
									card.preferred_printing_id = null;
									card.cards = null;
								}
							}
						}

						// OPTIMISTIC: Update UI immediately
						card.quantity = card.printings.reduce(
							(sum, p) => sum + (p.normal_quantity || 0) + (p.foil_quantity || 0),
							0
						);

						// Remove card from deck if quantity reaches 0
						if (card.quantity === 0) {
							state.activeDeck.cards = state.activeDeck.cards.filter(
								(c) => c.canonical_card_id !== canonicalCardId
							);
						}

						set({ activeDeck: { ...state.activeDeck, cards: [...state.activeDeck.cards] } });
					}

					return;
				}

				// For saved decks, enqueue the update to serialize writes
				const targetPrintingId =
					card.preferred_printing_id ||
					card.printings?.[0]?.printing_id ||
					(Array.isArray(card.cards) ? card.cards[0]?.id : card.cards?.id);

				if (!targetPrintingId) {
					toast.error('Please select a printing first');
					return;
				}

				// Enqueue update to prevent overlapping writes
				await enqueueCardUpdate(canonicalCardId, async () => {
					const currentState = get();
					const currentCard = currentState.activeDeck?.cards.find(
						(c) => c.canonical_card_id === canonicalCardId
					);
					if (!currentCard || !currentState.activeDeck?.id) return;

					if (delta > 0) {
						// INCREMENT using atomic RPC
						const printing = currentCard.printings?.find((p) => p.printing_id === targetPrintingId);
						const isFoil = printing?.cards?.finish === 'foil';

						// OPTIMISTIC: Update UI immediately
						if (printing) {
							if (isFoil) {
								printing.foil_quantity = (printing.foil_quantity || 0) + delta;
							} else {
								printing.normal_quantity = (printing.normal_quantity || 0) + delta;
							}
							printing.quantity = printing.normal_quantity + printing.foil_quantity;
						}

						currentCard.quantity =
							currentCard.printings?.reduce(
								(sum, p) => sum + (p.normal_quantity || 0) + (p.foil_quantity || 0),
								0
							) || 0;
						set({
							activeDeck: { ...currentState.activeDeck, cards: [...currentState.activeDeck.cards] }
						});

						// BACKGROUND: Update database with new quantities
						try {
							const newNormal = printing ? printing.normal_quantity : isFoil ? 0 : delta;
							const newFoil = printing ? printing.foil_quantity : isFoil ? delta : 0;

							await get().updatePrintingAndQuantities(
								canonicalCardId,
								targetPrintingId,
								newNormal,
								newFoil
							);

							// Schedule throttled reload
							scheduleReload(currentState.activeDeck.id, get);
						} catch (error) {
							console.error('Failed to increment card:', error);
							toast.error('Failed to save changes. Please try again.');
							// Reload to fix inconsistency
							await get().reloadDeck();
						}
					} else {
						// DECREMENT
						if (!currentCard.printings || currentCard.printings.length === 0) return;

						const sortedPrintings = [...currentCard.printings].sort((a, b) => {
							const qtyA = (a.normal_quantity || 0) + (a.foil_quantity || 0);
							const qtyB = (b.normal_quantity || 0) + (b.foil_quantity || 0);
							return qtyB - qtyA;
						});

						const printing = sortedPrintings.find((p) => p.normal_quantity + p.foil_quantity > 0);
						if (!printing) return;

						const printingId = printing.printing_id;
						let remaining = Math.abs(delta);
						let newNormal = printing.normal_quantity;
						let newFoil = printing.foil_quantity;

						const isFoil = printing.cards?.finish === 'foil';
						if (isFoil) {
							if (newFoil > 0) {
								const toRemove = Math.min(remaining, newFoil);
								newFoil -= toRemove;
								remaining -= toRemove;
							}
							if (remaining > 0 && newNormal > 0) {
								const toRemove = Math.min(remaining, newNormal);
								newNormal -= toRemove;
							}
						} else {
							if (newNormal > 0) {
								const toRemove = Math.min(remaining, newNormal);
								newNormal -= toRemove;
								remaining -= toRemove;
							}
							if (remaining > 0 && newFoil > 0) {
								const toRemove = Math.min(remaining, newFoil);
								newFoil -= toRemove;
							}
						}

						// OPTIMISTIC: Update UI immediately
						printing.normal_quantity = newNormal;
						printing.foil_quantity = newFoil;
						printing.quantity = newNormal + newFoil;

						if (printing.quantity === 0) {
							currentCard.printings = currentCard.printings?.filter(
								(p) => p.printing_id !== printingId
							);

							if (currentCard.preferred_printing_id === printingId) {
								if (currentCard.printings && currentCard.printings.length > 0) {
									currentCard.preferred_printing_id = currentCard.printings[0].printing_id;
									currentCard.cards = currentCard.printings[0].cards;
								} else {
									currentCard.preferred_printing_id = null;
									currentCard.cards = null;
								}
							}
						}

						currentCard.quantity =
							currentCard.printings?.reduce(
								(sum, p) => sum + (p.normal_quantity || 0) + (p.foil_quantity || 0),
								0
							) || 0;

						if (currentCard.quantity === 0) {
							currentState.activeDeck.cards = currentState.activeDeck.cards.filter(
								(c) => c.canonical_card_id !== canonicalCardId
							);
						}

						set({
							activeDeck: { ...currentState.activeDeck, cards: [...currentState.activeDeck.cards] }
						});

						// BACKGROUND: Sync to database
						try {
							await get().updatePrintingAndQuantities(
								canonicalCardId,
								printingId,
								newNormal,
								newFoil
							);

							// Schedule throttled reload
							if (currentState.activeDeck?.id) {
								scheduleReload(currentState.activeDeck.id, get);
							}
						} catch (error) {
							console.error('Failed to sync card quantity:', error);
							toast.error('Failed to save changes. Please try again.');
							await get().reloadDeck();
						}
					}
				});
			},

			updatePrintingAndQuantities: async (
				canonicalCardId: string,
				printingId: string | null,
				normalQty: number,
				foilQty: number
			) => {
				const state = get();
				if (!state.activeDeck) return;

				try {
					// Find the parent card
					const parentCard = state.activeDeck.cards.find(
						(c) => c.canonical_card_id === canonicalCardId
					);
					if (!parentCard) return;

					// UNSAVED DECK: Update local state
					if (!state.activeDeck.id) {
						if (!printingId) {
							// Remove all printings for this card
							parentCard.printings = [];
							parentCard.quantity = 0;
							set({ activeDeck: { ...state.activeDeck, cards: [...state.activeDeck.cards] } });
							return;
						}

						const totalQty = normalQty + foilQty;

						// Initialize printings array if needed
						if (!parentCard.printings) {
							parentCard.printings = [];
						}

						// Find existing printing
						const existingPrinting = parentCard.printings.find((p) => p.printing_id === printingId);

						if (totalQty === 0) {
							// Remove this printing
							parentCard.printings = parentCard.printings.filter(
								(p) => p.printing_id !== printingId
							);
						} else if (existingPrinting) {
							// Update existing printing
							existingPrinting.normal_quantity = normalQty;
							existingPrinting.foil_quantity = foilQty;
							existingPrinting.quantity = normalQty + foilQty;
						} else {
							// Add new printing - fetch the printing data first
							const { data: printing } = await supabase
								.from('cards')
								.select('*, sets(name, code, icon_url)')
								.eq('id', printingId)
								.single();

							if (printing) {
								parentCard.printings.push({
									id: crypto.randomUUID(),
									deck_id: '',
									canonical_card_id: canonicalCardId,
									printing_id: printingId,
									normal_quantity: normalQty,
									foil_quantity: foilQty,
									quantity: normalQty + foilQty,
									cards: printing,
									created_at: new Date().toISOString(),
									updated_at: new Date().toISOString()
								});
							}
						}

						// Recalculate parent total
						parentCard.quantity = parentCard.printings.reduce(
							(sum, p) => sum + (p.normal_quantity || 0) + (p.foil_quantity || 0),
							0
						);

						// Update preferred_printing_id and display cards if setting a new printing
						if (totalQty > 0) {
							parentCard.preferred_printing_id = printingId;

							// Update display cards to match preferred printing
							const match = parentCard.printings.find((p) => p.printing_id === printingId);
							if (match?.cards) {
								parentCard.cards = match.cards;
							} else {
								// Fetch if not in memory
								const { data: printing } = await supabase
									.from('cards')
									.select('*, sets(name, code, icon_url)')
									.eq('id', printingId)
									.single();
								if (printing) parentCard.cards = printing;
							}
						}

						set({ activeDeck: { ...state.activeDeck, cards: [...state.activeDeck.cards] } });
						return;
					}

					// SAVED DECK: Database operations
					if (!printingId) {
						// Clear all printings for this canonical card
						await supabase
							.from('deck_card_printings')
							.delete()
							.eq('deck_id', state.activeDeck.id)
							.eq('canonical_card_id', canonicalCardId);

						// Delete parent row entirely (don't set quantity to 0)
						await supabase
							.from('deck_cards')
							.delete()
							.eq('deck_id', state.activeDeck.id)
							.eq('canonical_card_id', canonicalCardId);

						await get().reloadDeck();
						return;
					}

					// Clamp quantities to >= 0
					const clampedNormalQty = Math.max(0, normalQty);
					const clampedFoilQty = Math.max(0, foilQty);
					const totalQty = clampedNormalQty + clampedFoilQty;

					// Fetch canonical_card_id from printing to ensure alignment
					const { data: printingData } = await supabase
						.from('cards')
						.select('canonical_card_id')
						.eq('id', printingId)
						.maybeSingle();

					const resolvedCanonicalId = printingData?.canonical_card_id || canonicalCardId;

					if (resolvedCanonicalId !== canonicalCardId) {
						console.warn('[deckStore] Canonical mismatch detected:', {
							expected: canonicalCardId,
							fromPrinting: resolvedCanonicalId,
							printingId
						});
					}

					// Fetch existing row by deck_id + printing_id
					const { data: existingRow } = await supabase
						.from('deck_card_printings')
						.select('id, canonical_card_id, normal_quantity, foil_quantity')
						.eq('deck_id', state.activeDeck.id)
						.eq('printing_id', printingId)
						.maybeSingle();

					console.debug('[deckStore] updatePrintingAndQuantities - action:', {
						printingId,
						existingRow: existingRow ? 'found' : 'not found',
						totalQty,
						inputNormal: clampedNormalQty,
						inputFoil: clampedFoilQty
					});

					if (totalQty === 0) {
						// Delete this printing
						if (existingRow) {
							await supabase.from('deck_card_printings').delete().eq('id', existingRow.id);
						}
						// No-op if row doesn't exist
					} else if (existingRow) {
						// Update existing row by id (explicit update, no upsert)
						const { error } = await supabase
							.from('deck_card_printings')
							.update({
								normal_quantity: clampedNormalQty,
								foil_quantity: clampedFoilQty
							})
							.eq('id', existingRow.id);

						if (error) throw error;
					} else {
						// Insert new row - but first check if adding this would exceed 4 total
						const { data: otherPrintings } = await supabase
							.from('deck_card_printings')
							.select('normal_quantity, foil_quantity')
							.eq('deck_id', state.activeDeck.id)
							.eq('canonical_card_id', resolvedCanonicalId)
							.neq('printing_id', printingId);

						const otherTotal =
							otherPrintings?.reduce(
								(sum, p) => sum + (p.normal_quantity || 0) + (p.foil_quantity || 0),
								0
							) || 0;

						if (otherTotal + totalQty > 4) {
							throw new Error(
								`Cannot exceed 4 total copies. Other printings: ${otherTotal}, attempting to add: ${totalQty}`
							);
						}

						const { error } = await supabase.from('deck_card_printings').insert({
							deck_id: state.activeDeck.id,
							canonical_card_id: resolvedCanonicalId,
							printing_id: printingId,
							normal_quantity: clampedNormalQty,
							foil_quantity: clampedFoilQty
						});

						if (error) throw error;
					}

					// Recalculate parent normal/foil quantities from all printings
					const { data: allPrintings } = await supabase
						.from('deck_card_printings')
						.select('normal_quantity, foil_quantity')
						.eq('deck_id', state.activeDeck.id)
						.eq('canonical_card_id', canonicalCardId);

					const normalSum =
						allPrintings?.reduce((sum, p) => sum + (p.normal_quantity || 0), 0) || 0;
					const foilSum = allPrintings?.reduce((sum, p) => sum + (p.foil_quantity || 0), 0) || 0;

					console.debug('[deckStore] updatePrintingAndQuantities - DB readback:', {
						canonicalCardId,
						printingId,
						inputNormal: clampedNormalQty,
						inputFoil: clampedFoilQty,
						dbNormalSum: normalSum,
						dbFoilSum: foilSum,
						totalCards: normalSum + foilSum
					});

					// If total is 0, delete the parent row
					if (normalSum === 0 && foilSum === 0) {
						await supabase
							.from('deck_cards')
							.delete()
							.eq('deck_id', state.activeDeck.id)
							.eq('canonical_card_id', canonicalCardId);
					} else {
						// Update parent's normal/foil quantities, total quantity, and preferred_printing_id
						await supabase
							.from('deck_cards')
							.update({
								normal_quantity: normalSum,
								foil_quantity: foilSum,
								quantity: normalSum + foilSum,
								preferred_printing_id: printingId
							})
							.eq('deck_id', state.activeDeck.id)
							.eq('canonical_card_id', canonicalCardId);
					}

					// Reload deck without resetting baseline
					await get().reloadDeck();
				} catch (error: any) {
					console.error('[deckStore] Error updating printing:', error);

					// Check for RLS policy violation (deck no longer accessible)
					if (error.code === '42501') {
						get().clearDeck();
						toast.error('Deck is no longer accessible');
						return;
					}

					if (error.message?.includes('exceed 4')) {
						toast.error('Cannot have more than 4 total copies of any card');
					} else {
						toast.error('Failed to update printing');
					}

					// Always reload deck to ensure UI matches DB state
					if (state.activeDeck?.id) {
						await get().reloadDeck();
					}
				}
			},

			setPreferredPrinting: async (canonicalCardId: string, printingId: string) => {
				const state = get();
				if (!state.activeDeck) return;

				// Update local state with new preferred printing and display cards
				set({
					activeDeck: {
						...state.activeDeck,
						cards: state.activeDeck.cards.map((card) => {
							if (card.canonical_card_id === canonicalCardId) {
								// Find the preferred printing in printings array
								const pref = card.printings?.find((p) => p.printing_id === printingId);
								return {
									...card,
									preferred_printing_id: printingId,
									cards: pref?.cards || card.cards
								};
							}
							return card;
						})
					}
				});

				// Update database if deck is saved
				if (state.activeDeck.id) {
					const { error } = await supabase
						.from('deck_cards')
						.update({ preferred_printing_id: printingId })
						.eq('deck_id', state.activeDeck.id)
						.eq('canonical_card_id', canonicalCardId);

					if (error) {
						toast.error('Failed to set default art');
						console.error('Error setting preferred printing:', error);
					} else {
						toast.success('Default art updated');
						// Trigger hydration to fetch the new preferred printing data
						get().hydrateCardPrinting(canonicalCardId);
					}
				}
			},

			setParagon: (canonicalCardId: string | null) => {
				const state = get();
				if (!state.activeDeck) return;

				set({
					activeDeck: {
						...state.activeDeck,
						paragon_canonical_id: canonicalCardId
					}
				});
			},

			updateDeckName: (name: string) => {
				const state = get();
				if (!state.activeDeck) return;

				set({
					activeDeck: { ...state.activeDeck, name }
				});
			},

			updateDeckDescription: (description: string) => {
				const state = get();
				if (!state.activeDeck) return;

				set({
					activeDeck: { ...state.activeDeck, description }
				});
			},

			setDeckPublic: async (isPublic: boolean, validation?: DeckValidation) => {
				const state = get();
				if (!state.activeDeck) return;

				// Block making public if deck has errors
				if (isPublic && validation && !validation.canMakePublic) {
					const errorMessages = validation.errors.map((e) => e.message).join(', ');
					toast.error(`Cannot make deck public: ${errorMessages}`);
					return;
				}

				// Keep all visibility fields in sync and auto-publish when made public
				const visibility = isPublic ? 'public' : 'private';
				const status = isPublic ? 'published' : 'draft';

				set({
					activeDeck: { ...state.activeDeck, is_public: isPublic, visibility, status }
				});

				// If deck is saved, update in DB
				if (state.activeDeck.id) {
					const { error } = await supabase
						.from('decks' as any)
						.update({ is_public: isPublic, visibility, status })
						.eq('id', state.activeDeck.id);

					if (error) {
						console.error('Error updating deck visibility:', error);
						toast.error('Failed to update deck visibility');
					} else {
						const message = isPublic
							? 'Deck is now public and published'
							: 'Deck is now private and set to draft';
						toast.success(message);
					}
				}
			},

			setDeckFormat: (format: 'core' | 'infinity') => {
				const state = get();
				if (!state.activeDeck) return;

				set({
					activeDeck: { ...state.activeDeck, format }
				});
			},

			setDeckVisibility: async (visibility: 'private' | 'unlisted' | 'public') => {
				const state = get();
				if (!state.activeDeck) return;

				// Auto-publish when made public
				const status = visibility === 'public' ? 'published' : state.activeDeck.status;

				set({
					activeDeck: { ...state.activeDeck, visibility, status }
				});

				// If deck is saved, update in DB
				if (state.activeDeck.id) {
					const { error } = await supabase
						.from('decks' as any)
						.update({ visibility, status })
						.eq('id', state.activeDeck.id);

					if (error) {
						console.error('Error updating deck visibility:', error);
						toast.error('Failed to update deck visibility');
					} else {
						const message =
							visibility === 'public'
								? 'Deck is now public and published'
								: `Deck is now ${visibility}`;
						toast.success(message);
					}
				}
			},

			setDeckStatus: async (status: 'draft' | 'published' | 'archived') => {
				const state = get();
				if (!state.activeDeck) return;

				set({
					activeDeck: { ...state.activeDeck, status }
				});

				// If deck is saved, update in DB
				if (state.activeDeck.id) {
					const { error } = await supabase
						.from('decks' as any)
						.update({ status })
						.eq('id', state.activeDeck.id);

					if (error) {
						console.error('Error updating deck status:', error);
						toast.error('Failed to update deck status');
					} else {
						toast.success(`Deck status: ${status}`);
					}
				}
			},

			// @deprecated - No longer used. Status is now automatically set in saveDeck based on visibility.
			// Kept for backward compatibility only.
			publishDeck: async (validation: DeckValidation) => {
				const state = get();
				if (!state.activeDeck) return;

				// Block publishing if deck has errors
				if (!validation.canMakePublic) {
					const errorMessages = validation.errors.map((e) => e.message).join(', ');
					toast.error(`Cannot publish deck: ${errorMessages}`);
					return;
				}

				// Save deck first if not saved
				if (!state.activeDeck.id) {
					await get().saveDeck();
					const newState = get();
					if (!newState.activeDeck?.id) {
						toast.error('Failed to save deck');
						return;
					}
				}

				// Update status and visibility
				set({
					activeDeck: {
						...state.activeDeck,
						status: 'published',
						visibility: 'public'
					}
				});

				const { data, error } = await supabase
					.from('decks' as any)
					.update({ status: 'published', visibility: 'public' })
					.eq('id', state.activeDeck.id)
					.select('slug')
					.single();

				if (error) {
					console.error('Error publishing deck:', error);
					toast.error('Failed to publish deck');
				} else {
					const slug = (data as any)?.slug || state.activeDeck.id;
					const url = `${window.location.origin}/decks/${slug}`;
					toast.success('Deck published!', {
						description: url,
						action: {
							label: 'Copy Link',
							onClick: () => {
								navigator.clipboard.writeText(url);
								toast.success('Link copied to clipboard!');
							}
						}
					});
				}
			},

			saveDeck: async () => {
				const state = get();
				if (!state.activeDeck) return;

				// Prevent concurrent saves
				if (state.isSaving) {
					console.log('[deckStore] Save already in progress, skipping');
					return;
				}

				// Check authentication
				const {
					data: { user }
				} = await supabase.auth.getUser();
				if (!user) {
					toast.error('Please sign in to save decks');
					return;
				}

				set({ isLoading: true, isSaving: true });
				try {
					// Validate deck name
					const { deckNameSchema } = await import('@/lib/validationSchemas');
					const nameValidation = deckNameSchema.safeParse(state.activeDeck.name);

					if (!nameValidation.success) {
						const errorMessage = nameValidation.error.errors[0]?.message || 'Invalid deck name';
						toast.error('Cannot save deck', { description: errorMessage });
						set({ isLoading: false });
						return;
					}

					// Drain all pending updates before saving
					await drainPendingAndQueues(get);

				// Automatically determine status based on visibility
				// Public decks are 'published', private/unlisted are 'draft'
				// TODO: Implement deck versioning to track edit history when published decks are edited
				const calculatedStatus: 'draft' | 'published' =
					state.activeDeck.visibility === 'public' ? 'published' : 'draft';

				// Compute default hero image: most expensive card, or random fallback
				const computeDefaultHero = (cards: DeckCard[]): string | null => {
					let bestPrintingId: string | null = null;
					let highestPrice = 0;
					const allPrintingIds: string[] = [];

					for (const card of cards) {
						for (const printing of card.printings || []) {
							const printingData = printing.cards;
							if (!printingData) continue;
							
							allPrintingIds.push(printing.printing_id);
							
							const normalPrice = printingData.price_usd ?? 0;
							const foilPrice = printingData.price_usd_foil ?? 0;
							const maxPrice = Math.max(normalPrice, foilPrice);

							if (maxPrice > highestPrice) {
								highestPrice = maxPrice;
								bestPrintingId = printing.printing_id;
							}
						}
					}

					// If no prices found, pick a random printing as fallback
					if (!bestPrintingId && allPrintingIds.length > 0) {
						const randomIndex = Math.floor(Math.random() * allPrintingIds.length);
						bestPrintingId = allPrintingIds[randomIndex];
					}

					return bestPrintingId;
				};

				const deckData = {
					name: state.activeDeck.name || 'Untitled Deck',
					description: state.activeDeck.description,
					paragon_canonical_id: state.activeDeck.paragon_canonical_id,
					format: state.activeDeck.format,
					is_public: state.activeDeck.is_public,
					visibility: state.activeDeck.visibility,
					status: calculatedStatus,
					hero_printing_id: state.activeDeck.hero_printing_id,
					computed_hero_printing_id: computeDefaultHero(state.activeDeck.cards)
				} as any;

					let deckId = state.activeDeck.id;

					if (!deckId) {
						// Create new deck
						const { data: user } = await supabase.auth.getUser();
						if (!user.user) throw new Error('Not authenticated');

						const { data: newDeck, error: deckError } = await supabase
							.from('decks')
							.insert({ ...deckData, user_id: user.user.id })
							.select()
							.single();

						if (deckError) throw deckError;
						deckId = newDeck.id;

						// Insert parent cards (filter out cards with 0 quantity)
						const cardsToSave = state.activeDeck.cards.filter((card) => card.quantity > 0);

						// Prepare all parent cards for batch insert
						const parentCards = cardsToSave
							.filter((card) => {
								const normalTotal = (card.printings || []).reduce(
									(sum, p) => sum + (p.normal_quantity || 0),
									0
								);
								const foilTotal = (card.printings || []).reduce(
									(sum, p) => sum + (p.foil_quantity || 0),
									0
								);
								return normalTotal + foilTotal > 0;
							})
							.map((card) => {
								const normalTotal = (card.printings || []).reduce(
									(sum, p) => sum + (p.normal_quantity || 0),
									0
								);
								const foilTotal = (card.printings || []).reduce(
									(sum, p) => sum + (p.foil_quantity || 0),
									0
								);
								return {
									deck_id: deckId,
									canonical_card_id: card.canonical_card_id,
									preferred_printing_id: card.preferred_printing_id,
									normal_quantity: normalTotal,
									foil_quantity: foilTotal
								};
							});

						// Single batch insert for all parent cards
						if (parentCards.length > 0) {
							const { error: parentError } = await supabase.from('deck_cards').insert(parentCards);

							if (parentError) {
								console.error('[saveDeck] Parent batch insert error:', parentError);
								throw parentError;
							}
						}

						// Prepare all child printings for batch insert
						const allPrintings = cardsToSave.flatMap((card) =>
							(card.printings || [])
								.filter((p) => (p.normal_quantity || 0) + (p.foil_quantity || 0) > 0)
								.map((printing) => ({
									deck_id: deckId,
									canonical_card_id: card.canonical_card_id,
									printing_id: printing.printing_id,
									normal_quantity: printing.normal_quantity || 0,
									foil_quantity: printing.foil_quantity || 0
								}))
						);

						// Single batch insert for all printings
						if (allPrintings.length > 0) {
							const { error: childError } = await supabase
								.from('deck_card_printings')
								.insert(allPrintings);

							if (childError) {
								console.error('[saveDeck] Child batch insert error:', childError);
								throw childError;
							}
						}

						// Reconcile deck cards and refresh stats
						await supabase.rpc('reconcile_deck_cards', { p_deck_id: deckId });

						// Record copy lineage if this deck was copied from another
						if (state.activeDeck.sourceDeckId) {
							const { data: user } = await supabase.auth.getUser();
							if (user.user) {
								// Insert copy record
								await supabase.from('deck_copies').insert({
									source_deck_id: state.activeDeck.sourceDeckId,
									destination_deck_id: deckId,
									copied_by_user_id: user.user.id
								});

								// Fetch and increment copy count on source deck
								const { data: sourceDeck } = await supabase
									.from('decks')
									.select('copy_count')
									.eq('id', state.activeDeck.sourceDeckId)
									.single();

								if (sourceDeck) {
									await supabase
										.from('decks')
										.update({ copy_count: (sourceDeck.copy_count || 0) + 1 })
										.eq('id', state.activeDeck.sourceDeckId);
								}
							}
						}

						toast.success('Deck created successfully');
						// Update baseline to match saved state
						const updatedDeck = { ...state.activeDeck, id: deckId };
						set({
							activeDeck: updatedDeck,
							baselineCards: JSON.parse(JSON.stringify(updatedDeck.cards))
						});
						// Clear deck builder and return deck info for navigation
						set({ activeDeck: null, baselineCards: null, isPanelOpen: false });
						return { success: true, deckId, slug: (newDeck as any).slug || null };
					} else {
						// Update existing deck
						// Pending updates already drained at start of saveDeck

						// Compare baseline vs current state to detect card changes
						const { compareDeckStates, hasCardChanges } = await import('@/lib/deckVersioning');
						const baselineCardsList = state.baselineCards || [];
						const newCardsList = state.activeDeck.cards.filter((c) => c.quantity > 0);

						if (hasCardChanges(baselineCardsList, newCardsList)) {
							// Create a version snapshot before updating
							const { data: user } = await supabase.auth.getUser();
							if (!user.user) throw new Error('Not authenticated');

							// Get the next version number
							const { data: latestVersion } = await supabase
								.from('deck_versions')
								.select('version_number')
								.eq('deck_id', deckId)
								.order('version_number', { ascending: false })
								.limit(1)
								.maybeSingle();

							const nextVersionNumber = (latestVersion?.version_number || 0) + 1;

							// Generate change summary
							const changeSummary = compareDeckStates(baselineCardsList, newCardsList);

							// Get current deck metadata
							const { data: currentDeck } = await supabase
								.from('decks')
								.select('*')
								.eq('id', deckId)
								.single();

							if (currentDeck) {
								// Create version record
								const { data: newVersion, error: versionError } = await supabase
									.from('deck_versions')
									.insert([
										{
											deck_id: deckId,
											version_number: nextVersionNumber,
											created_by_user_id: user.user.id,
											change_summary: changeSummary as any,
											name: currentDeck.name,
											description: currentDeck.description,
											format: currentDeck.format,
											paragon_canonical_id: currentDeck.paragon_canonical_id,
											hero_printing_id: currentDeck.hero_printing_id
										}
									])
									.select()
									.single();

								if (versionError) {
									console.error('Error creating version:', versionError);
								} else if (newVersion) {
									// Snapshot baseline cards (state before changes)
									const versionCards = baselineCardsList.map((card) => ({
										version_id: newVersion.id,
										canonical_card_id: card.canonical_card_id,
										quantity: card.quantity,
										preferred_printing_id: card.preferred_printing_id
									}));

									if (versionCards.length > 0) {
										await supabase.from('deck_version_cards').insert(versionCards);
									}

									// Snapshot current printings
									const { data: currentPrintings } = await supabase
										.from('deck_card_printings')
										.select('*')
										.eq('deck_id', deckId);

									if (currentPrintings && currentPrintings.length > 0) {
										const versionPrintings = currentPrintings.map((p) => ({
											version_id: newVersion.id,
											canonical_card_id: p.canonical_card_id,
											printing_id: p.printing_id,
											normal_quantity: p.normal_quantity,
											foil_quantity: p.foil_quantity
										}));

										await supabase.from('deck_version_printings').insert(versionPrintings);
									}
								}
							}
						}

						// Now update the deck
						const { error: deckError } = await supabase
							.from('decks')
							.update(deckData)
							.eq('id', deckId);

						if (deckError) throw deckError;

						// Reconcile deck cards and refresh stats
						await supabase.rpc('reconcile_deck_cards', { p_deck_id: deckId });

						toast.success('Deck updated successfully');
						// Update baseline to current state
						set({ baselineCards: JSON.parse(JSON.stringify(state.activeDeck.cards)) });
						// Clear deck builder and return deck info for navigation
						set({ activeDeck: null, baselineCards: null, isPanelOpen: false });
						return { success: true, deckId, slug: null };
					}
				} catch (error) {
					console.error('Error saving deck:', error);
					toast.error('Failed to save deck');
				} finally {
					set({ isLoading: false, isSaving: false });
				}
			},

			deleteDeck: async () => {
				const state = get();
				if (!state.activeDeck?.id) return;

				console.log('[deckStore] Deleting deck:', state.activeDeck.id, state.activeDeck.name);

				try {
					const { error } = await supabase.from('decks').delete().eq('id', state.activeDeck.id);

					if (error) throw error;

					// Clear persisted state after successful deletion
					get().clearDeck();
					toast.success('Deck deleted');
				} catch (error) {
					console.error('[deckStore] Error deleting deck:', error);
					toast.error('Failed to delete deck');
				}
			},

			setHeroPrinting: (printingId: string) => {
				const state = get();
				if (!state.activeDeck) return;

				// Validate that the printing exists in the deck
				const existsInDeck = state.activeDeck.cards.some((card) =>
					card.printings?.some((p) => p.printing_id === printingId)
				);

				if (!existsInDeck) {
					toast.error('Cannot set hero image: card not in deck');
					return;
				}

				set({
					activeDeck: { ...state.activeDeck, hero_printing_id: printingId }
				});

				toast.success('Hero image updated');
			},

			clearDeck: () => {
				// Clear hero state explicitly when clearing deck
				set({
					activeDeck: null,
					baselineCards: null,
					isPanelOpen: false
				});
			}
		}),
		{
			name: 'deck-storage',
			partialize: (state) => ({
				activeDeck: state.activeDeck
			})
		}
	)
);
